import type { ReactNode } from 'react';
import { useT } from '@/shared/i18n';
import { Icon } from '@/shared/ui/icon';
import { cn } from '@/shared/lib/cn';
import type { SupportedLocale } from '@/app/config/app.config';

/**
 * Public-shell locale toggle — Languages icon + short ISO label of the
 * locale the user will switch to.
 *
 * The visible label intentionally shows the *target* locale ("EN" / "AR"),
 * not the current one, matching the theme-toggle's same target-state
 * convention. The accessible name remains direction-explicit ("Switch
 * language" / "تغيير اللغة") so screen readers announce the action, not
 * the abbreviation.
 *
 * Sized to visually balance the 36 px theme toggle: same height, +icon-gap
 * for the trailing 2-letter label. Token-only colors. Logical-property
 * utilities only (the layout naturally mirrors under `[dir="rtl"]`).
 */
const controlClasses =
  'inline-flex h-9 items-center justify-center gap-2 rounded-md ' +
  'bg-transparent ' +
  'px-3 ' +
  'text-sm font-medium text-[var(--text-primary)] ' +
  'transition-colors duration-base ease-standard ' +
  'hover:bg-[var(--surface-raised)] ' +
  'focus-visible:outline-none';

export function PublicLocaleToggle(): ReactNode {
  const { t, locale, setLocale } = useT();
  const otherLocale: SupportedLocale = locale === 'en' ? 'ar' : 'en';
  const shortLabel =
    otherLocale === 'en' ? t('locale.short.en') : t('locale.short.ar');
  return (
    <button
      type="button"
      onClick={() => setLocale(otherLocale)}
      aria-label={t('locale.toggle.aria.label')}
      data-testid="locale-toggle"
      className={cn(controlClasses)}
    >
      <Icon name="languages" size={18} />
      <span aria-hidden="true">{shortLabel}</span>
    </button>
  );
}

export default PublicLocaleToggle;
